import { filterClasses } from "@swivl-mono/helpers";
import { motion } from "motion/react";
import { ChangeEvent, forwardRef, useEffect, useRef, useState } from "react";
import { HiOutlineSearch } from "react-icons/hi";
import { IoClose } from "react-icons/io5";

import { Button, ButtonVariant } from "../Button/Button";
import { getFormFieldWidthClass } from "../FormField/FormField.Helpers";
import { FormFieldWidth } from "../FormField/FormField.Types";
import type { InputProps } from "./Input";
import { InputSize } from "./Input";
import { InputField } from "./Input.Field";
import { InputSuffix } from "./Input.Suffix";

export interface SearchInputProps extends Omit<InputProps, "prefix" | "suffix"> {
  onClear?: () => void;
  isExpandable?: boolean;
}

const normalizeInputValue = (inputValue: unknown): string => {
  if (typeof inputValue === "string") {
    return inputValue;
  }

  if (typeof inputValue === "number") {
    return String(inputValue);
  }

  return "";
};

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      onClear,
      value,
      defaultValue,
      isExpandable = false,
      inputSize = InputSize.MEDIUM,
      error,
      disabled,
      placeholder = "Search",
      containerClassName,
      width = FormFieldWidth.FULL,
      label,
      helperText,
      required,
      labelPosition,
      expandable,
      onChange,
      onSuffixClick,
      ...props
    },
    ref
  ) => {
    // An expandable box that mounts with a value opens expanded: the value is already filtering
    // whatever list it belongs to, and a collapsed empty box would hide that (ENG-3809).
    const [isExpanded, setIsExpanded] = useState(!isExpandable || normalizeInputValue(value ?? defaultValue) !== "");
    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = useState<string>(() => normalizeInputValue(defaultValue));
    const resolvedValue = isControlled ? normalizeInputValue(value) : internalValue;
    const hasValue = resolvedValue !== "";
    const inputRef = useRef<HTMLInputElement>(null);

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) {
        setInternalValue(event.target.value);
      }

      onChange?.(event);
    };

    const handleClear = () => {
      if (!isControlled) {
        setInternalValue("");
      }

      if (onClear) {
        onClear();
        return;
      }

      onChange?.({ target: { value: "" } } as ChangeEvent<HTMLInputElement>);
    };

    // for expandable, use w-fit so container grows/shrinks with animated inner width
    // non-expandable uses the width prop normally
    const containerWidthClass = isExpandable ? (isExpanded ? "w-full" : "w-fit") : getFormFieldWidthClass(width);
    const resolvedContainerClassName = filterClasses(containerWidthClass, containerClassName);

    // focus input on expansion
    useEffect(() => {
      if (isExpanded && isExpandable) {
        const timer = setTimeout(() => {
          inputRef.current?.focus();
        }, 120);
        return () => clearTimeout(timer);
      }
    }, [isExpanded, isExpandable]);

    const handleToggle = () => {
      if (disabled) return;

      if (!isExpanded) {
        setIsExpanded(true);
        return;
      }

      if (hasValue) {
        handleClear();
        setIsExpanded(false);
      } else {
        setIsExpanded(false);
      }
    };

    const handleSuffixClick = () => {
      if (disabled) return;

      if (onSuffixClick) {
        onSuffixClick();
        return;
      }

      if (hasValue) {
        handleClear();
      }
    };

    const buttonSizeClasses = {
      [InputSize.SMALL]: "h-[26px] p-1",
      [InputSize.MEDIUM]: "h-[34px] p-2"
    };

    if (!isExpandable) {
      return (
        <div className={filterClasses("relative", resolvedContainerClassName)}>
          <InputField
            disabled={disabled}
            ref={ref ?? inputRef}
            type="search"
            value={resolvedValue}
            placeholder={placeholder}
            hasSuffix
            inputSize={inputSize}
            error={!!error}
            onChange={handleInputChange}
            {...props}
          />
          <InputSuffix onClick={handleSuffixClick} inputSize={inputSize} disabled={disabled}>
            {hasValue ? <IoClose className="size-4" /> : <HiOutlineSearch className="size-4" />}
          </InputSuffix>
        </div>
      );
    }

    return (
      <div className={resolvedContainerClassName}>
        <motion.div className="flex items-center" initial={false} transition={{ duration: 0.22, ease: "easeOut" }}>
          {isExpanded && (
            <>
              <div className="relative flex-1 overflow-hidden rounded-sm">
                <InputField
                  disabled={disabled}
                  ref={ref ?? inputRef}
                  type="search"
                  value={resolvedValue}
                  placeholder={placeholder}
                  hasSuffix
                  inputSize={inputSize}
                  error={!!error}
                  onChange={handleInputChange}
                  {...props}
                />
                <InputSuffix onClick={handleSuffixClick} inputSize={inputSize} disabled={disabled}>
                  {hasValue ? <IoClose className="size-4" /> : <HiOutlineSearch className="size-4" />}
                </InputSuffix>
              </div>

              <Button onClick={handleToggle} variant={ButtonVariant.GHOST}>
                Cancel
              </Button>
            </>
          )}
          {!isExpanded && (
            <button
              className={filterClasses(
                buttonSizeClasses[inputSize],
                "cursor-pointer transition-colors duration-200 ease-in-out hover:text-orange-500",
                "data-[disabled=true]:cursor-not-allowed data-[disabled=true]:text-grey-500 data-[disabled=true]:hover:text-grey-500"
              )}
              data-disabled={disabled || undefined}
              onClick={handleToggle}
            >
              <HiOutlineSearch className="size-4" />
            </button>
          )}
        </motion.div>
      </div>
    );
  }
);

SearchInput.displayName = "SearchInput";
